"use client";

// Compact snow report form for a ski location: where on the mountain, snow cover, surface
// and an optional 0 to 10 rating. Posts to /api/snow-checkins. Not mounted by this module;
// the ski detail page's "Who's here" panel is the intended home (beside CheckInButton).

import Link from "next/link";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import type { SurfaceTag } from "@/lib/conditions/ski/types";
import {
  SNOW_CHECKIN_BANDS,
  SNOW_CHECKIN_BAND_LABELS,
  SNOW_CHECKIN_COVERS,
  SNOW_CHECKIN_COVER_LABELS,
  SNOW_CHECKIN_SURFACES,
  SNOW_CHECKIN_SURFACE_LABELS,
  type SnowCheckinBand,
  type SnowCheckinCover,
} from "@/lib/conditions/snow-checkins";

export type SnowCheckinFormStatus =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "rate-limited"; message: string }
  | { kind: "signed-out"; message: string }
  | { kind: "error"; message: string };

export type SnowCheckinDraft = {
  band: SnowCheckinBand | null;
  cover: SnowCheckinCover | null;
  surface: SurfaceTag | null;
  rating: number | null;
};

const RATING_ANCHORS: Readonly<Record<number, string>> = { 0: "terrible", 5: "average", 10: "perfect" };
const RATINGS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const SNOW_CHECKIN_FORM_COPY = {
  success: "Thanks for your snow report. It helps everyone find the best snow today.",
  rateLimited: "You have shared a snow report recently. Please try again later.",
  signedOut: "Log in to share a snow report.",
  error: "We could not send your snow report. Check your connection and try again.",
  hint: "Choose where you are, then add the snow cover, the surface or a rating.",
  runHint: "Add the snow cover, the surface or a rating. Where you are on the mountain is optional.",
} as const;

/** Client request timeout; the route's own database budget is shorter. */
export const SNOW_CHECKIN_REQUEST_TIMEOUT_MS = 15_000;

/**
 * A report needs a target (a band, or the run the form was opened for) and at least one of
 * cover, surface or rating, matching validateSnowCheckin.
 */
export function canSubmitSnowCheckin(draft: SnowCheckinDraft, runId: string | null = null): boolean {
  const hasTarget = draft.band !== null || (runId !== null && runId.length > 0);
  return hasTarget && (draft.cover !== null || draft.surface !== null || draft.rating !== null);
}

export function snowCheckinRequestBody(locationId: string, runId: string | null, draft: SnowCheckinDraft) {
  return {
    locationId,
    ...(runId ? { runId } : {}),
    band: draft.band,
    cover: draft.cover,
    surface: draft.surface,
    rating: draft.rating,
  };
}

function serverMessage(payload: unknown): string | null {
  if (payload === null || typeof payload !== "object") return null;
  const error = (payload as Record<string, unknown>).error;
  return typeof error === "string" && error.length > 0 && error.length <= 300 ? error : null;
}

/** Maps an API response to the form state. Pure, so it is unit tested without a DOM. */
export function snowCheckinStatusFromResponse(status: number, payload: unknown): SnowCheckinFormStatus {
  if (status === 200 || status === 201) return { kind: "success" };
  if (status === 401) return { kind: "signed-out", message: SNOW_CHECKIN_FORM_COPY.signedOut };
  if (status === 429) return { kind: "rate-limited", message: serverMessage(payload) ?? SNOW_CHECKIN_FORM_COPY.rateLimited };
  if (status === 400 || status === 404) return { kind: "error", message: serverMessage(payload) ?? SNOW_CHECKIN_FORM_COPY.error };
  return { kind: "error", message: SNOW_CHECKIN_FORM_COPY.error };
}

const chipClass =
  "relative inline-flex min-h-11 min-w-11 cursor-pointer select-none items-center justify-center rounded-full border border-border bg-white px-4 text-[13px] font-semibold text-navy transition-colors hover:border-navy/40 has-[:checked]:border-navy has-[:checked]:bg-navy has-[:checked]:text-white has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-teal has-[:focus-visible]:ring-offset-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60";

function Chip({
  name,
  value,
  checked,
  disabled,
  onSelect,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <label className={chipClass}>
      <input
        type="radio"
        className="sr-only"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      {children}
    </label>
  );
}

function Group({ legend, hint, children }: { legend: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset className="mt-4 min-w-0 border-0 p-0">
      <legend className="text-[13px] font-semibold text-navy">
        {legend}
        {hint ? <span className="ml-1 font-normal text-text-muted">{hint}</span> : null}
      </legend>
      <div className="mt-2 flex flex-wrap gap-2">{children}</div>
    </fieldset>
  );
}

/** Presentational status text. Live-region semantics come from the containers in the form. */
export function SnowCheckinStatusNotice({ status }: { status: SnowCheckinFormStatus }) {
  if (status.kind === "success") {
    return <span className="block rounded-sm bg-emerald-50 px-3 py-2.5 text-[13px] text-emerald-900">{SNOW_CHECKIN_FORM_COPY.success}</span>;
  }
  if (status.kind === "rate-limited") {
    return <span className="block rounded-sm bg-amber-50 px-3 py-2.5 text-[13px] text-navy">{status.message}</span>;
  }
  if (status.kind === "signed-out" || status.kind === "error") {
    return <span className="block rounded-sm bg-red-50 px-3 py-2.5 text-[13px] text-red-800">{status.message}</span>;
  }
  return null;
}

export function SnowCheckinForm({
  locationId,
  runId: runIdProp = null,
  isLoggedIn,
  loginRedirectPath,
}: {
  locationId: string;
  /** Optional run the report is about (a planner run page, for example). */
  runId?: string | null;
  /** Overrides the SpotUserProvider login state (for pages outside the provider). */
  isLoggedIn?: boolean;
  /** Where to return after logging in, for example "/conditions/val-disere". */
  loginRedirectPath?: string;
}) {
  const spotUser = useSpotUser();
  const loggedIn = isLoggedIn ?? spotUser.isLoggedIn;
  // An empty run id means no run, so the band stays required.
  const runId = runIdProp ? runIdProp : null;
  const baseId = useId();
  const headingId = `${baseId}-heading`;
  const hintId = `${baseId}-hint`;
  const ratingId = `${baseId}-rating`;

  const [draft, setDraft] = useState<SnowCheckinDraft>({ band: null, cover: null, surface: null, rating: null });
  const [status, setStatus] = useState<SnowCheckinFormStatus>({ kind: "idle" });
  const statusRef = useRef<HTMLDivElement>(null);

  // The form is replaced on success, so move focus to the confirmation.
  useEffect(() => {
    if (status.kind === "success") statusRef.current?.focus();
  }, [status.kind]);

  const submitting = status.kind === "submitting";
  const ready = canSubmitSnowCheckin(draft, runId);
  const loginHref = loginRedirectPath ? `/login?redirect=${encodeURIComponent(loginRedirectPath)}` : "/login";
  const update = (patch: Partial<SnowCheckinDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    if (status.kind === "error" || status.kind === "rate-limited") setStatus({ kind: "idle" });
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || submitting) return;
    setStatus({ kind: "submitting" });
    try {
      const response = await fetch("/api/snow-checkins", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(snowCheckinRequestBody(locationId, runId, draft)),
        // A stalled request moves to the error state instead of leaving the form disabled.
        signal: AbortSignal.timeout(SNOW_CHECKIN_REQUEST_TIMEOUT_MS),
      });
      const payload: unknown = await response.json().catch(() => null);
      setStatus(snowCheckinStatusFromResponse(response.status, payload));
    } catch {
      setStatus({ kind: "error", message: SNOW_CHECKIN_FORM_COPY.error });
    }
  }

  const showSignIn = !loggedIn || status.kind === "signed-out";

  return (
    <section aria-labelledby={headingId} className="rounded-[16px] border border-border-light bg-white p-5 md:p-6">
      <h3 id={headingId} className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
        Snow report
      </h3>
      <p className="mt-1 text-[13.5px] text-text-body">What is the snow like where you are?</p>

      {status.kind === "success" ? null : showSignIn ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-text-muted">{SNOW_CHECKIN_FORM_COPY.signedOut}</p>
          <Link
            href={loginHref}
            className="inline-flex min-h-11 items-center rounded-sm border border-navy/15 px-4 text-[13px] font-semibold text-navy no-underline hover:bg-navy/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
          >
            Log in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate aria-describedby={hintId}>
          <Group legend="Where are you on the mountain?" hint={runId ? "(optional)" : undefined}>
            {runId ? (
              <Chip
                name={`${baseId}-band`}
                value=""
                checked={draft.band === null}
                disabled={submitting}
                onSelect={() => update({ band: null })}
              >
                Not sure
              </Chip>
            ) : null}
            {SNOW_CHECKIN_BANDS.map((band) => (
              <Chip
                key={band}
                name={`${baseId}-band`}
                value={band}
                checked={draft.band === band}
                disabled={submitting}
                onSelect={() => update({ band })}
              >
                {SNOW_CHECKIN_BAND_LABELS[band]}
              </Chip>
            ))}
          </Group>

          <Group legend="Snow cover">
            <Chip
              name={`${baseId}-cover`}
              value=""
              checked={draft.cover === null}
              disabled={submitting}
              onSelect={() => update({ cover: null })}
            >
              Not sure
            </Chip>
            {SNOW_CHECKIN_COVERS.map((cover) => (
              <Chip
                key={cover}
                name={`${baseId}-cover`}
                value={cover}
                checked={draft.cover === cover}
                disabled={submitting}
                onSelect={() => update({ cover })}
              >
                {SNOW_CHECKIN_COVER_LABELS[cover]}
              </Chip>
            ))}
          </Group>

          <Group legend="Surface">
            <Chip
              name={`${baseId}-surface`}
              value=""
              checked={draft.surface === null}
              disabled={submitting}
              onSelect={() => update({ surface: null })}
            >
              Not sure
            </Chip>
            {SNOW_CHECKIN_SURFACES.map((surface) => (
              <Chip
                key={surface}
                name={`${baseId}-surface`}
                value={surface}
                checked={draft.surface === surface}
                disabled={submitting}
                onSelect={() => update({ surface })}
              >
                {SNOW_CHECKIN_SURFACE_LABELS[surface]}
              </Chip>
            ))}
          </Group>

          <div className="mt-4">
            <label htmlFor={ratingId} className="text-[13px] font-semibold text-navy">
              Overall rating <span className="font-normal text-text-muted">(optional)</span>
            </label>
            <select
              id={ratingId}
              value={draft.rating === null ? "" : String(draft.rating)}
              disabled={submitting}
              onChange={(event) => update({ rating: event.target.value === "" ? null : Number(event.target.value) })}
              className="mt-2 block min-h-11 w-full rounded-sm border border-border bg-white px-3 text-[14px] text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal sm:w-56"
            >
              <option value="">No rating</option>
              {RATINGS.map((value) => (
                <option key={value} value={String(value)}>
                  {RATING_ANCHORS[value] ? `${value} (${RATING_ANCHORS[value]})` : String(value)}
                </option>
              ))}
            </select>
          </div>

          <p id={hintId} className="mt-4 text-[12px] text-text-muted">
            {runId ? SNOW_CHECKIN_FORM_COPY.runHint : SNOW_CHECKIN_FORM_COPY.hint}
          </p>

          <button
            type="submit"
            disabled={!ready || submitting}
            className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-sm bg-teal px-5 text-[14px] font-semibold text-white transition-colors hover:bg-teal-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {submitting ? "Sharing…" : "Share snow report"}
          </button>

          <div role="alert" className="mt-4 empty:hidden">
            {status.kind === "error" ? <SnowCheckinStatusNotice status={status} /> : null}
          </div>
        </form>
      )}

      <div
        ref={statusRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className="mt-4 empty:hidden focus:outline-none"
      >
        {status.kind === "success" || status.kind === "rate-limited" ? <SnowCheckinStatusNotice status={status} /> : null}
      </div>
    </section>
  );
}
